import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Button,
  InputGroup,
  Table,
  Form
} from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import Paging from 'components/common-sfims/pagination/paging';
import moment from 'moment';
import columns from 'components/pages/haccp/monitoring/common/page/rawMaterial/rawMaterialResult/columns';

//function
import { datas2SfshExcelJSON } from 'function/convert';
import { downloadExcelByJSON } from 'function/file';
// types
import { RESPONSE_CODE } from 'types/const';
import { RESULT_TYPE_SELECT } from 'types/type';
// api
import { factory } from 'api/haccp/commonApi';
import { rwmtrResultList } from 'api/haccp/monitoring/rawMaterialApi';
// util
import format from 'utils/string/format';
import comma from 'utils/format/comma';
// hooks
import useHistoryState from 'hooks/useHistoryState';

/**
 * @description 원료- 검사결과 목록
 */

const List = () => {
  /* router info*/
  const history = useHistory();
  const { state } = useLocation();

  /* state */
  const [inspTypeNm] = useState(state?.inspTypeNm ?? '');
  const [totalCount, setTotalCount] = useState(0);
  const [factoryOptions, setFactoryOptions] = useState(null);
  const [list, setList] = useState([]);

  /* 조회조건 */
  // 공장
  const [pltCd, setPltCd] = useHistoryState('', 'pltCd');
  // 검사결과 TYPE
  const [inspResultType, setInspResultType] = useHistoryState(
    '',
    'inspResultType'
  );
  // 구분선택 key
  const [divisionKey, setDivisionKey] = useHistoryState('', 'divisionKey');
  // input value
  const [divisionValue, setDivisionValue] = useHistoryState(
    '',
    'divisionValue'
  );
  // 조회시작일
  const [regDtSt, setRegDtSt] = useHistoryState(null, 'regDtSt');
  // 조회종료일
  const [regDtEn, setRegDtEn] = useHistoryState(null, 'regDtEn');
  const [searchParams, setSearchParams] = useHistoryState(
    {
      page: 0,
      size: 11,
      sort: 'inspReqDt,desc',
      minDate: regDtSt, // Date Picker 달력범위 시작일
      maxDate: regDtEn, // Date Picker 달력범위 종료일
      ...state
    },
    'searchParams'
  );

  /* variable */
  const title = `${inspTypeNm} 검사결과`;

  /* event */

  // 모니터링 페이지 이동
  const onMonitoring = () => {
    const factory = pltCd === 'A110' ? 'icheon' : 'gunsan';
    history.push({
      pathname: `/haccp/monitoring/${factory}`,
      state: state
    });
  };

  // 원료 - 검사결과 상세페이지 이동
  const onDetail = seq => {
    history.push({
      pathname: format('/haccp/monitoring/rawMaterial/result/detail/{seq}', {
        seq
      }),
      state: searchParams
    });
  };

  // 조회일 선택
  const onDateChange = dates => {
    const [start, end] = dates;
    setRegDtSt(start);
    setRegDtEn(end);
  };

  /* 엑셀다운로드 */
  const onExcelDown = async () => {
    const {
      baseYm,
      pltCd,
      ctgryGrpCd,
      inspTypeSeq,
      inspResultType = '',
      divisionKey = '',
      divisionValue = '',
      regDtSt = null,
      regDtEn = null,
      sort
    } = searchParams || {};

    const { year, month, inspReqDtSt, inspReqDtEn } = dateTypeConverter(
      baseYm,
      regDtSt,
      regDtEn
    );

    const params = {
      pltCd: pltCd,
      ctgryGrpCd: ctgryGrpCd,
      inspTypeSeq: inspTypeSeq,
      year: Number(year),
      month: month,
      inspResultType: inspResultType,
      [divisionKey]: divisionValue,
      inspReqDtSt: inspReqDtSt,
      inspReqDtEn: inspReqDtEn,
      sort: sort
    };

    Object.keys(params).forEach(
      key => (!params[key] || params[key] === 'ALL') && delete params[key]
    );

    const response = await rwmtrResultList(params);

    const { code, list = [], totalCount = 0 } = response || {};

    if (code !== RESPONSE_CODE.Success || totalCount === 0) return;

    let excelNumber = totalCount;

    downloadExcelByJSON(
      datas2SfshExcelJSON(
        list?.map(e => ({
          ...e,
          no: excelNumber--
        })),
        columns
      ),
      `${title}.xlsx`
    );
  };

  // 조회버튼
  const onSearch = () => {
    setSearchParams({
      ...searchParams,
      page: 0,
      pltCd: pltCd,
      inspResultType: inspResultType,
      divisionKey: divisionKey,
      divisionValue: divisionValue,
      regDtSt: regDtSt,
      regDtEn: regDtEn
    });
  };

  // 조회 API호출
  const onSearchRequest = async () => {
    const {
      baseYm,
      pltCd,
      ctgryGrpCd,
      inspTypeSeq,
      inspResultType = '',
      divisionKey = '',
      divisionValue = '',
      regDtSt = null,
      regDtEn = null,
      page,
      size,
      sort
    } = searchParams || {};

    const { year, month, inspReqDtSt, inspReqDtEn } = dateTypeConverter(
      baseYm,
      regDtSt,
      regDtEn
    );

    const params = {
      pltCd: pltCd,
      ctgryGrpCd: ctgryGrpCd,
      inspTypeSeq: inspTypeSeq,
      year: Number(year),
      month: month,
      inspResultType: inspResultType,
      [divisionKey]: divisionValue,
      inspReqDtSt: inspReqDtSt,
      inspReqDtEn: inspReqDtEn,
      page: page,
      size: size,
      sort: sort
    };

    Object.keys(params).forEach(
      key =>
        (params[key] === null || params[key] === '' || params[key] === 'ALL') &&
        delete params[key]
    );

    const response = await rwmtrResultList(params);

    const { code, list, totalCount } = response || {};

    if (code !== RESPONSE_CODE.Success) return;

    let indexNo = totalCount - searchParams.size * searchParams.page;

    setTotalCount(totalCount);
    setList(
      list?.map(e => ({
        no: indexNo--,
        ...e
      })) ?? []
    );
  };

  /**
   * 조회연월 : baseYm
   * 검사결과의뢰 시작일: regDtSt
   * 검사결과의뢰 종료일: regDtEn
   */
  const dateTypeConverter = (baseYm, regDtSt, regDtEn) => {
    const year = moment(regDtSt).format('YYYY');
    const month = moment(regDtSt).format('MM');

    let inspReqDtSt = regDtSt;
    let inspReqDtEn = regDtEn;

    if (inspReqDtSt !== null) {
      inspReqDtSt = moment(regDtSt).format('YYYY-MM-DD');
    }

    if (inspReqDtEn !== null) {
      inspReqDtEn = moment(regDtEn).format('YYYY-MM-DD');
    }
    return { year, month, inspReqDtSt, inspReqDtEn };
  };

  // 공장목록 API
  const factoryList = async () => {
    const response = await factory();

    const { code, list } = response || {};
    if (code !== RESPONSE_CODE.Success) return;

    setFactoryOptions(list);
  };

  /* watch */
  useEffect(() => {
    // 공장 API 호출
    factoryList();
  }, []);

  useEffect(() => {
    // 조회API 호출
    onSearchRequest();
  }, [searchParams]);

  useEffect(() => {
    // 모니터링 > 한계기준이탈내역 페이지 이동 시 조회월 default Setting
    const { baseYm, regDtSt = null, regDtEn = null } = state || {};

    if (regDtSt === null || regDtEn === null) {
      setRegDtSt(moment(baseYm).startOf('month').toDate());
      setRegDtEn(moment(baseYm).endOf('month').toDate());
    }
  }, []);

  return (
    <>
      <Card>
        <Card.Header>
          <Row className="align-items-center g-2">
            <Col>
              <h5 className="mb-0">[{inspTypeNm || '-'}] 검사결과</h5>
            </Col>
            <Col xs={'auto'}>
              <Button
                size="sm"
                variant="secondary"
                className="ms-2"
                onClick={onExcelDown}
                disabled={totalCount === 0}
              >
                엑셀 다운로드
              </Button>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body className="pt-0">
          <Form.Group className="mb-3">
            <Row className="ms-0 me-0 mt-3">
              {/* 공장 선택 select-box */}
              <Col sm={2} className="ps-1 pe-1">
                <Form.Select
                  size="sm"
                  onChange={({ target: { value } }) => setPltCd(value)}
                  value={pltCd}
                  disabled={true}
                >
                  {factoryOptions?.map(option => (
                    <option key={option.pltCd} value={option.pltCd}>
                      {option.pltNmKo}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              {/* 검사결과 TYPE select-box */}
              <Col sm={2} className="ps-1 pe-1">
                <Form.Select
                  size="sm"
                  onChange={({ target: { value } }) => setInspResultType(value)}
                  value={inspResultType}
                >
                  <option disabled value="">
                    검사결과 TYPE
                  </option>
                  <option value={'ALL'}>전체</option>
                  {RESULT_TYPE_SELECT?.map(option => (
                    <option key={option.key} value={option.key}>
                      {option.value}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col sm={4} className="ps-1 pe-1">
                <InputGroup size="sm" className="w-100">
                  <Form.Select
                    size="sm"
                    className="w-25"
                    value={divisionKey}
                    onChange={({ target: { value } }) => setDivisionKey(value)}
                  >
                    <option hidden>선택</option>
                    <option value={'ctgry3Nm'}>등록명칭번호</option>
                    <option value={'sampleNm'}>품목명</option>
                  </Form.Select>
                  <Form.Control
                    placeholder="구분선택 후 입력해주세요."
                    type="text"
                    size="sm"
                    className="w-50"
                    maxLength="20"
                    value={divisionValue}
                    onChange={({ target: { value } }) =>
                      setDivisionValue(value)
                    }
                    disabled={divisionKey === ''}
                  />
                </InputGroup>
              </Col>
              {/* 조회기간 */}
              <Col sm={2} className="ps-1 pe-1">
                <DatePicker
                  locale="ko"
                  onChange={onDateChange}
                  startDate={regDtSt}
                  endDate={regDtEn}
                  formatWeekDay={day => day.slice(0, 1)}
                  selectsRange
                  dateFormat="yyyy-MM-dd"
                  className="form-control form-control-sm"
                  placeholderText="검사결과 의뢰일 선택"
                  minDate={searchParams.minDate}
                  maxDate={searchParams.maxDate}
                />
              </Col>
              <Col
                sm={2}
                className="ps-1 pe-1 d-flex flex-row align-items-center"
              >
                <Button size="sm" onClick={onSearch}>
                  조회
                </Button>
                <p className="mb-0 ms-3 fs--1 fw-bold lh-1">
                  검색 결과 : {comma(totalCount)}건
                </p>
              </Col>
            </Row>
          </Form.Group>
          <Table responsive bordered hover className="fs--1 text-center">
            <thead className="bg-200 text-700 align-middle border-300">
              <tr>
                <th>No.</th>
                <th>검사결과 TYPE</th>
                <th>자재번호</th>
                <th>외부자재번호</th>
                <th>원료명</th>
                <th>모선명</th>
                <th>검사결과 의뢰일</th>
                <th>검사결과 등록일</th>
              </tr>
            </thead>
            <tbody>
              {list?.map(
                (
                  {
                    no,
                    seq,
                    inspResultTypeNm,
                    ctgry1Nm,
                    ctgry2Nm,
                    sampleNm,
                    cshipNm,
                    inspReqDt,
                    regDttm
                  },
                  index
                ) => (
                  <tr
                    key={index}
                    onClick={() => onDetail(seq)}
                    className="cursor-pointer"
                  >
                    <td>{no}</td>
                    <td>{inspResultTypeNm || '-'}</td>
                    <td>{ctgry1Nm || '-'}</td>
                    <td>{ctgry2Nm || '-'}</td>
                    <td>{sampleNm || '-'}</td>
                    <td>{cshipNm || '-'}</td>
                    <td>{inspReqDt || '-'}</td>
                    <td>{regDttm || '-'}</td>
                  </tr>
                )
              )}
              {list.length < 1 && (
                <tr>
                  <td colSpan={8} className="w-auto">
                    검사결과 정보가 존재하지 않습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
          {totalCount > 0 && (
            <Paging
              key={totalCount}
              perPage={searchParams.size}
              length={10}
              currentPage={searchParams.page + 1}
              totalCount={totalCount}
              movePage={page =>
                setSearchParams({ ...searchParams, page: page - 1 })
              }
            ></Paging>
          )}
        </Card.Body>
        <Card.Footer className="d-flex">
          <Button size="sm" className="ms-auto" onClick={onMonitoring}>
            이전
          </Button>
        </Card.Footer>
      </Card>
    </>
  );
};

export default List;
